import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, formatINR } from "../lib/api";
import { loadDraft, saveDraft, clearDraft } from "../lib/billingDraft";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Search, Plus, Trash2, User, Sparkles, Star, Banknote, Smartphone, CreditCard, Building2,
  Receipt, Loader2, Printer, MessageCircle, Share2, ScanLine, X, KeyboardIcon,
  CheckCircle2, RotateCcw, ArrowLeft, History, Pencil, Tag, Send,
} from "lucide-react";
import { toast } from "sonner";
import UpiQrDialog from "../components/UpiQrDialog";
import { buildWhatsAppLink, TEMPLATES, wa } from "../lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METHODS = [
  { key: "cash", label: "Cash", icon: Banknote, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { key: "upi", label: "UPI", icon: Smartphone, color: "text-violet-600 bg-violet-50 border-violet-200" },
  { key: "card", label: "Card", icon: CreditCard, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { key: "bank_transfer", label: "Bank", icon: Building2, color: "text-slate-700 bg-slate-50 border-slate-200" },
];

function useDebounced(value, ms = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function BillingNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const clinicId = user?.clinic_id || "default";

  // ----- Patient search -----
  const [patientQuery, setPatientQuery] = useState("");
  const dq = useDebounced(patientQuery, 200);
  const [patientResults, setPatientResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);

  // ----- Services catalog -----
  const [services, setServices] = useState([]);
  const [favs, setFavs] = useState([]);
  const [recents, setRecents] = useState([]);
  const [serviceQuery, setServiceQuery] = useState("");
  const sq = useDebounced(serviceQuery, 160);

  // ----- Line items + totals -----
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState({ kind: "amount", value: 0 });
  const [taxPct, setTaxPct] = useState(0);

  // ----- Payments -----
  const [payments, setPayments] = useState([]); // [{method, amount}]
  const [splitOpen, setSplitOpen] = useState(false);

  // ----- Settings/clinic -----
  const [clinicName, setClinicName] = useState("our clinic");
  const [upiVpa, setUpiVpa] = useState("");
  const [upiPayeeName, setUpiPayeeName] = useState("");

  // ----- UI state -----
  const [saving, setSaving] = useState(false);
  const [successInvoice, setSuccessInvoice] = useState(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [qrInvoice, setQrInvoice] = useState(null);
  const [waConfigured, setWaConfigured] = useState(false);

  const patientInputRef = useRef(null);
  const serviceInputRef = useRef(null);

  // ----- AI-lite service suggestions -----
  const [suggestions, setSuggestions] = useState([]);
  // ----- Clinic-wide recent bills (when no patient selected) -----
  const [clinicRecents, setClinicRecents] = useState([]);

  // ----- Load clinic settings + services + draft -----
  useEffect(() => {
    api.get("/clinic/settings").then(({ data }) => {
      setClinicName(data?.name || "our clinic");
      setUpiVpa(data?.upi_vpa || "");
      setUpiPayeeName(data?.upi_payee_name || data?.name || "");
    }).catch(() => {});
    api.get("/services?limit=200").then(({ data }) => setServices(data.items || [])).catch(() => {});
    api.get("/services/recent").then(({ data }) => {
      setFavs(data.favorites || []);
      setRecents(data.recent || []);
    }).catch(() => {});
    wa.status().then((s) => setWaConfigured(!!s.configured)).catch(() => {});
    api.get("/invoices/recent?limit=6").then(({ data }) => setClinicRecents(data.items || [])).catch(() => {});

    // Restore draft — prefer localStorage (instant), fall back to backend (cross-device).
    const d = loadDraft(clinicId);
    if (d) {
      setPatient(d.patient || null);
      setItems(d.items || []);
      setDiscount(d.discount || { kind: "amount", value: 0 });
      setTaxPct(d.taxPct || 0);
      setPayments(d.payments || []);
      if (d.patient) toast.info("Restored unsaved draft", { duration: 2500 });
    } else {
      api.get("/billing/draft").then(({ data }) => {
        if (data && !data.empty && (data.patient || (data.items || []).length > 0)) {
          setPatient(data.patient || null);
          setItems(data.items || []);
          setDiscount(data.discount || { kind: "amount", value: 0 });
          setTaxPct(data.taxPct || 0);
          setPayments(data.payments || []);
          toast.info("Restored draft from another device", { duration: 2500 });
        }
      }).catch(() => {});
    }
    setTimeout(() => patientInputRef.current?.focus(), 100);
  }, []);

  // ----- Patient search live -----
  useEffect(() => {
    if (!dq || patient) { setPatientResults([]); return; }
    api.get("/patients", { params: { q: dq, limit: 8 } }).then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.items || []);
      setPatientResults(list);
    }).catch(() => {});
  }, [dq, patient]);

  // ----- Patient invoice history for "Duplicate previous" -----
  useEffect(() => {
    if (!patient?.patient_id) { setPatientHistory([]); setSuggestions([]); return; }
    api.get("/invoices", { params: { limit: 10 } }).then(({ data }) => {
      const list = (data.items || []).filter((x) => x.patient_id === patient.patient_id).slice(0, 5);
      setPatientHistory(list);
    }).catch(() => {});
    api.get(`/services/suggest?patient_id=${patient.patient_id}&limit=4`).then(({ data }) => {
      setSuggestions(data.suggestions || []);
    }).catch(() => {});
  }, [patient?.patient_id]);

  // ----- Auto-save draft (localStorage + backend, debounced) -----
  useEffect(() => {
    saveDraft(clinicId, { patient, items, discount, taxPct, payments });
  }, [clinicId, patient, items, discount, taxPct, payments]);

  useEffect(() => {
    // Debounced backend draft persistence — survives device switch / browser wipe.
    const t = setTimeout(() => {
      if (!patient && items.length === 0) return; // nothing worth saving
      api.put("/billing/draft", { patient, items, discount, taxPct, payments }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [patient, items, discount, taxPct, payments]);

  // ----- Totals -----
  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.amount) || (Number(it.quantity) || 1) * (Number(it.unit_price) || 0)), 0),
    [items]
  );
  const discountAmount = useMemo(() => {
    const v = Number(discount.value) || 0;
    if (v <= 0) return 0;
    if (discount.kind === "percent") return Math.min(subtotal, +(subtotal * v / 100).toFixed(2));
    return Math.min(subtotal, v);
  }, [subtotal, discount]);
  const taxable = Math.max(subtotal - discountAmount, 0);
  const taxAmount = useMemo(() => +(taxable * (Number(taxPct) || 0) / 100).toFixed(2), [taxable, taxPct]);
  const total = +(taxable + taxAmount).toFixed(2);
  const paidSum = useMemo(() => payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [payments]);
  const balance = +Math.max(total - paidSum, 0).toFixed(2);

  // ----- Service catalog filter -----
  const filteredServices = useMemo(() => {
    if (!sq) return services;
    const ql = sq.toLowerCase();
    return services.filter((s) => s.name.toLowerCase().includes(ql) || (s.code || "").toLowerCase().includes(ql));
  }, [services, sq]);

  // ----- Actions -----
  const addServiceLine = useCallback((svc, opts = {}) => {
    setItems((prev) => {
      // If already in list, just increment quantity
      const idx = prev.findIndex((p) => p.service_id === svc.service_id);
      if (idx >= 0 && !opts.duplicate) {
        const next = [...prev];
        const q = (Number(next[idx].quantity) || 1) + 1;
        next[idx] = { ...next[idx], quantity: q, amount: +(q * (Number(next[idx].unit_price) || 0)).toFixed(2) };
        return next;
      }
      return [
        ...prev,
        {
          uid: `li_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
          service_id: svc.service_id,
          description: svc.name,
          quantity: 1,
          unit_price: svc.default_price,
          amount: svc.default_price,
        },
      ];
    });
    setServiceQuery("");
    serviceInputRef.current?.focus();
  }, []);

  const addCustomLine = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        uid: `li_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
        description: "Custom charge",
        quantity: 1,
        unit_price: 0,
        amount: 0,
      },
    ]);
  }, []);

  const updateItem = (uid, patch) => {
    setItems((prev) => prev.map((it) => {
      if (it.uid !== uid) return it;
      const merged = { ...it, ...patch };
      const q = Number(merged.quantity) || 1;
      const u = Number(merged.unit_price) || 0;
      merged.amount = +(q * u).toFixed(2);
      return merged;
    }));
  };

  const removeItem = (uid) => setItems((prev) => prev.filter((it) => it.uid !== uid));

  const toggleFavorite = async (svc) => {
    try {
      const { data } = await api.post(`/services/${svc.service_id}/favorite`);
      // Refresh favs/recents
      const r = await api.get("/services/recent");
      setFavs(r.data.favorites || []);
      setRecents(r.data.recent || []);
      toast.success(data.action === "added" ? "Pinned to favorites" : "Removed from favorites");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handlePayQuick = (method) => {
    if (total <= 0) { toast.error("Add a service first"); return; }
    setPayments([{ method, amount: total }]);
  };

  const handleSplit = () => {
    if (total <= 0) { toast.error("Add a service first"); return; }
    setSplitOpen(true);
  };

  // ----- Submit -----
  const submit = async () => {
    if (!patient?.patient_id) { toast.error("Pick a patient first"); patientInputRef.current?.focus(); return; }
    if (items.length === 0) { toast.error("Add at least one service / charge"); serviceInputRef.current?.focus(); return; }
    setSaving(true);
    try {
      const body = {
        patient_id: patient.patient_id,
        items: items.map(({ uid, ...rest }) => rest),
        discount: (Number(discount.value) || 0) > 0 ? discount : null,
        tax_pct: Number(taxPct) || 0,
        payments: payments.map((p) => ({ method: p.method, amount: Number(p.amount) || 0 })).filter((p) => p.amount > 0),
      };
      const { data } = await api.post("/invoices", body);
      clearDraft(clinicId);
      api.delete("/billing/draft").catch(() => {});
      setSuccessInvoice(data);
      // refresh recents + clinic recents
      api.get("/services/recent").then((r) => {
        setFavs(r.data.favorites || []);
        setRecents(r.data.recent || []);
      }).catch(() => {});
      api.get("/invoices/recent?limit=6").then((r) => setClinicRecents(r.data.items || [])).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setPatient(null);
    setPatientQuery("");
    setItems([]);
    setDiscount({ kind: "amount", value: 0 });
    setTaxPct(0);
    setPayments([]);
    setSuccessInvoice(null);
    clearDraft(clinicId);
    api.delete("/billing/draft").catch(() => {});
    setTimeout(() => patientInputRef.current?.focus(), 80);
  };

  // ----- Duplicate previous -----
  const duplicateFromPrev = (prev) => {
    if (prev?.items?.length) {
      setItems(prev.items.map((it) => ({
        uid: `li_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
        service_id: it.service_id,
        description: it.description,
        quantity: it.quantity || 1,
        unit_price: it.unit_price,
        amount: it.amount,
      })));
    } else if (prev?.amount) {
      setItems([{
        uid: `li_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
        description: "Consultation",
        quantity: 1,
        unit_price: prev.amount,
        amount: prev.amount,
      }]);
    }
    if (prev?.discount?.amount) {
      setDiscount({ kind: prev.discount.kind || "amount", value: prev.discount.value || prev.discount.amount });
    }
    if (prev?.tax_pct) setTaxPct(prev.tax_pct);
    setPayments([]);
    toast.success("Duplicated previous bill");
  };

  // ----- Keyboard shortcuts -----
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        patientInputRef.current?.focus();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!successInvoice) submit();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        if (successInvoice?.invoice_id) printInvoice(successInvoice.invoice_id);
      } else if (e.key === "?" && !isTyping) {
        e.preventDefault();
        setShortcutsOpen(true);
      } else if (e.key === "Escape") {
        if (shortcutsOpen) setShortcutsOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submit, successInvoice, shortcutsOpen]);

  const printInvoice = (invoice_id) => {
    const url = `${BACKEND_URL}/api/invoices/${invoice_id}/pdf`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const w = window.open(u, "_blank");
        if (w) w.addEventListener("load", () => { try { w.print(); } catch (_e) { /* noop */ } });
      })
      .catch(() => toast.error("Print failed"));
  };

  const shareWhatsApp = async (inv) => {
    if (!patient?.phone) { toast.error("Patient phone is missing"); return; }
    try {
      const { data } = await api.get(`/invoices/${inv.invoice_id}/share`);
      const link = `${window.location.origin}${data.share_path}`;
      const tokenLine = inv.token_number ? `\nYour token #: ${inv.token_number}` : "";
      const msg = `${TEMPLATES.invoiceShare({
        patient_name: inv.patient_name,
        clinic_name: clinicName,
        amount: inv.total ?? inv.amount,
        link,
      })}${tokenLine}`;
      window.open(buildWhatsAppLink(patient.phone, msg), "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Failed to share");
    }
  };

  // ============ RENDER ============
  return (
    <div className="min-h-[calc(100vh-1px)] bg-slate-50/40 fade-in" data-testid="billing-new-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1680px] mx-auto px-3 sm:px-4 lg:px-8 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
          <Link to="/billing" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3 h-3" /> <span className="hidden sm:inline">Back to billing</span>
          </Link>
          <div className="hidden sm:block h-4 w-px bg-slate-200 mx-1" />
          <h1 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900 flex-1 min-w-0 truncate" style={{ fontFamily: "Manrope" }}>
            New bill
            <span className="ml-2 text-[10px] sm:text-xs font-medium text-slate-400 hidden md:inline">⌘+S to save · / to focus search</span>
          </h1>
          <Button size="sm" variant="ghost" onClick={() => setShortcutsOpen(true)} className="text-xs h-8 px-2 sm:px-3" data-testid="open-shortcuts" title="Keyboard shortcuts">
            <KeyboardIcon className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Shortcuts</span>
          </Button>
          <Button size="sm" variant="outline" onClick={resetForm} className="text-xs h-8 px-2 sm:px-3" data-testid="reset-form" title="Reset form">
            <RotateCcw className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Reset</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1680px] mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 pb-24 lg:pb-6 grid grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
        {/* Column 1 — Patient */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <Card className="border-slate-200 lg:sticky lg:top-[68px]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4 text-slate-500" /> Patient
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!patient ? (
                <>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      ref={patientInputRef}
                      value={patientQuery}
                      onChange={(e) => setPatientQuery(e.target.value)}
                      placeholder="Name or phone…"
                      className="h-11 pl-9"
                      autoFocus
                      data-testid="patient-search-input"
                    />
                  </div>
                  {patientResults.length > 0 && (
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-auto" data-testid="patient-search-results">
                      {patientResults.map((p) => (
                        <button
                          key={p.patient_id}
                          onClick={() => { setPatient(p); setPatientQuery(""); setPatientResults([]); serviceInputRef.current?.focus(); }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                          data-testid={`patient-result-${p.patient_id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">
                            {(p.name || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">{p.phone}{p.age ? ` · ${p.age}y` : ""}{p.gender ? ` · ${p.gender}` : ""}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {dq && patientResults.length === 0 && (
                    <div className="text-xs text-slate-400 px-1 py-2">No matches. <Link to="/patients" className="text-blue-600 hover:underline">Create new patient</Link></div>
                  )}
                  {!dq && clinicRecents.length > 0 && (
                    <div className="pt-2">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 flex items-center gap-1.5">
                        <History className="w-3 h-3" /> Recent bills (clinic)
                      </div>
                      <div className="space-y-1 max-h-56 overflow-auto" data-testid="clinic-recents">
                        {clinicRecents.map((b) => (
                          <button
                            key={b.invoice_id}
                            onClick={async () => {
                              try {
                                // Fetch full patient object to load into the form
                                if (b.patient_id) {
                                  const { data } = await api.get(`/patients/${b.patient_id}`);
                                  setPatient(data);
                                  duplicateFromPrev(b);
                                }
                              } catch {
                                toast.error("Could not load patient");
                              }
                            }}
                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 flex items-center justify-between gap-2"
                            data-testid={`clinic-recent-${b.invoice_id}`}
                          >
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-slate-800 truncate">{b.patient_name}</div>
                              <div className="text-[10px] text-slate-500 truncate">
                                {formatINR(b.total ?? b.amount)}
                                {b.token_number ? ` · Token #${b.token_number}` : ""}
                                {" · "}{(b.created_at || "").slice(11, 16)}
                              </div>
                            </div>
                            <Plus className="w-3 h-3 text-blue-600 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3" data-testid="selected-patient">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                      {(patient.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate" data-testid="selected-patient-name">{patient.name}</div>
                      <div className="text-xs text-slate-600 truncate">{patient.phone}{patient.age ? ` · ${patient.age}y` : ""}</div>
                    </div>
                    <button onClick={() => setPatient(null)} className="text-slate-400 hover:text-red-600" data-testid="clear-patient" title="Change patient">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {patientHistory.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200/60">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-1.5 flex items-center gap-1.5">
                        <History className="w-3 h-3" /> Recent bills
                      </div>
                      <div className="space-y-1.5 max-h-44 overflow-auto">
                        {patientHistory.map((h) => (
                          <button
                            key={h.invoice_id}
                            onClick={() => duplicateFromPrev(h)}
                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-white/70 transition-colors flex items-center justify-between gap-2"
                            data-testid={`dup-prev-${h.invoice_id}`}
                          >
                            <div className="text-xs text-slate-700 truncate">
                              {formatINR(h.total ?? h.amount)} · <span className="text-slate-500">{(h.created_at || "").slice(0, 10)}</span>
                            </div>
                            <Plus className="w-3 h-3 text-blue-600 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Column 2 — Catalog + items */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          {/* Catalog */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                <Sparkles className="w-4 h-4 text-violet-500" /> Quick service
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  ref={serviceInputRef}
                  value={serviceQuery}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  placeholder="Search services or type to filter (press Enter to add top result)"
                  className="h-11 pl-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredServices[0]) {
                      e.preventDefault();
                      addServiceLine(filteredServices[0]);
                    }
                  }}
                  data-testid="service-search-input"
                />
              </div>

              {/* AI-lite suggestions (patient-scoped when possible) */}
              {suggestions.length > 0 && (
                <div data-testid="suggestions-block">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-violet-500" />
                    {patient ? `Suggested for ${patient.name?.split(" ")[0]}` : "Suggested"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s.service_id}
                        onClick={() => addServiceLine(s)}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-xs font-medium text-violet-800 hover:bg-violet-100 transition-colors"
                        title={s.reason || ""}
                        data-testid={`suggestion-${s.service_id}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        {s.name} · {formatINR(s.default_price)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorites + Recents */}
              {favs.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> Favorites
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {favs.map((s) => (
                      <ServiceChip key={s.service_id} svc={s} onClick={() => addServiceLine(s)} onPin={() => toggleFavorite(s)} pinned />
                    ))}
                  </div>
                </div>
              )}
              {recents.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 flex items-center gap-1.5">
                    <History className="w-3 h-3" /> Recently used
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {recents.slice(0, 6).map((s) => (
                      <ServiceChip key={s.service_id} svc={s} onClick={() => addServiceLine(s)} onPin={() => toggleFavorite(s)} pinned={favs.some((f) => f.service_id === s.service_id)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Search results */}
              {serviceQuery && (
                <div className="rounded-lg border border-slate-200 max-h-60 overflow-auto divide-y divide-slate-100" data-testid="service-search-results">
                  {filteredServices.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-slate-400">No services match. Add as a custom charge below.</div>
                  ) : filteredServices.slice(0, 10).map((s) => (
                    <button
                      key={s.service_id}
                      onClick={() => addServiceLine(s)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
                      data-testid={`service-result-${s.service_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                        {s.category && <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.category}</div>}
                      </div>
                      <div className="text-sm font-semibold text-slate-700 shrink-0">{formatINR(s.default_price)}</div>
                    </button>
                  ))}
                </div>
              )}

              <Button variant="outline" size="sm" onClick={addCustomLine} className="w-full" data-testid="add-custom-charge">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add custom charge
              </Button>
            </CardContent>
          </Card>

          {/* Line items */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-slate-500" /> Items
                {items.length > 0 && <Badge variant="outline" className="ml-auto bg-slate-50">{items.length}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {items.length === 0 ? (
                <div className="px-6 py-10 text-center" data-testid="items-empty">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Receipt className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">Pick a service above or add a custom charge.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <LineItemRow key={it.uid} item={it} onChange={(patch) => updateItem(it.uid, patch)} onRemove={() => removeItem(it.uid)} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Discount + tax */}
          {items.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-4 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><Tag className="w-3 h-3" /> Discount</Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                      {["amount", "percent"].map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setDiscount((d) => ({ ...d, kind: k }))}
                          className={`px-2 py-1.5 text-xs font-medium ${discount.kind === k ? "bg-blue-50 text-blue-700" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                          data-testid={`discount-kind-${k}`}
                        >
                          {k === "amount" ? "₹" : "%"}
                        </button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step={discount.kind === "percent" ? "1" : "10"}
                      value={discount.value}
                      onChange={(e) => setDiscount((d) => ({ ...d, value: e.target.value }))}
                      className="h-9 flex-1"
                      data-testid="discount-value"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">GST / Tax (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={taxPct}
                    onChange={(e) => setTaxPct(e.target.value)}
                    className="mt-1.5 h-9"
                    placeholder="0"
                    data-testid="tax-pct"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Column 3 — Live preview + checkout */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <div className="lg:sticky lg:top-[68px] space-y-4">
            {/* Live preview */}
            <Card className="border-slate-200 shadow-sm" data-testid="invoice-preview">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-700">Live preview</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="bg-white border-t border-slate-100 px-5 py-4 text-sm">
                  <div className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>{clinicName}</div>
                  <div className="text-xs text-slate-500 mt-3 flex items-center justify-between">
                    <span>INVOICE</span>
                    <span>{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <div className="text-xs text-slate-500">For: <span className="font-medium text-slate-700">{patient?.name || "—"}</span></div>
                  <div className="border-t border-slate-100 mt-3 pt-3 space-y-1">
                    {items.length === 0 ? (
                      <div className="text-xs text-slate-400 py-3 text-center">No items yet.</div>
                    ) : items.map((it) => (
                      <div key={it.uid} className="flex justify-between gap-2 text-xs">
                        <span className="text-slate-700 truncate">
                          {it.description}
                          {(Number(it.quantity) || 1) > 1 && <span className="text-slate-400"> × {it.quantity}</span>}
                        </span>
                        <span className="text-slate-900 font-medium shrink-0">{formatINR(it.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-100 mt-3 pt-3 space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium text-slate-700">{formatINR(subtotal)}</span></div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-medium text-emerald-700">− {formatINR(discountAmount)}</span></div>
                    )}
                    {taxAmount > 0 && (
                      <div className="flex justify-between"><span className="text-slate-500">GST @ {taxPct}%</span><span className="font-medium text-slate-700">{formatINR(taxAmount)}</span></div>
                    )}
                    <div className="flex justify-between border-t border-slate-100 pt-2 mt-1 text-base font-bold">
                      <span>Total</span>
                      <span className="text-blue-600" data-testid="preview-total">{formatINR(total)}</span>
                    </div>
                    {paidSum > 0 && (
                      <>
                        <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-medium text-emerald-700">{formatINR(paidSum)}</span></div>
                        {balance > 0 && <div className="flex justify-between"><span className="text-slate-500">Balance due</span><span className="font-semibold text-amber-700">{formatINR(balance)}</span></div>}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-700">Collect payment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  {METHODS.map((m) => {
                    const isOnly = payments.length === 1 && payments[0].method === m.key && Math.abs(payments[0].amount - total) < 0.01;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => handlePayQuick(m.key)}
                        disabled={total <= 0}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all text-xs ${isOnly ? `${m.color} border-current` : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"} disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`pay-${m.key}`}
                      >
                        <m.icon className="w-4 h-4" />
                        <span className="font-medium">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleSplit} disabled={total <= 0} data-testid="open-split-pay">
                  Split / partial payment…
                </Button>
                {payments.length > 1 && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 space-y-1">
                    {payments.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="text-slate-700 uppercase tracking-wide">{p.method}</span>
                        <span className="font-medium text-slate-900">{formatINR(Number(p.amount) || 0)}</span>
                      </div>
                    ))}
                    <button onClick={() => setPayments([])} className="text-[10px] text-slate-400 hover:text-red-600">Clear splits</button>
                  </div>
                )}
                {payments.some((p) => p.method === "upi" && p.amount > 0) && upiVpa.includes("@") && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setQrInvoice({ invoice_id: "preview", amount: payments.find((p) => p.method === "upi").amount, patient_name: patient?.name || "—", status: "unpaid" })}
                    className="w-full text-xs border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-700"
                    data-testid="show-upi-qr-preview"
                  >
                    <ScanLine className="w-3.5 h-3.5 mr-1.5" /> Show UPI QR for {formatINR(payments.find((p) => p.method === "upi")?.amount || 0)}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={submit}
              disabled={saving || !patient || items.length === 0}
              className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 hidden lg:flex"
              data-testid="submit-bill"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
              {paidSum > 0 ? `Collect ${formatINR(paidSum)} & generate bill` : `Save as unpaid · ${formatINR(total)}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom checkout — always reachable on tablets/phones */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 px-3 py-2.5 lg:hidden" data-testid="mobile-bottom-bar">
        <div className="max-w-[1680px] mx-auto flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">Total</div>
            <div className="text-lg font-bold text-slate-900 leading-tight" data-testid="mobile-bottom-total">{formatINR(total)}</div>
            {paidSum > 0 && balance > 0 && (
              <div className="text-[10px] text-amber-700 leading-none mt-0.5">Balance {formatINR(balance)}</div>
            )}
          </div>
          <Button
            onClick={submit}
            disabled={saving || !patient || items.length === 0}
            className="h-12 px-5 bg-blue-600 hover:bg-blue-700 shrink-0"
            data-testid="mobile-submit-bill"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {paidSum > 0 ? `Collect ${formatINR(paidSum)}` : "Save bill"}
          </Button>
        </div>
      </div>

      {/* Split payment dialog */}
      <SplitPaymentDialog
        open={splitOpen}
        onOpenChange={setSplitOpen}
        total={total}
        initial={payments}
        onApply={(p) => setPayments(p)}
        upiVpa={upiVpa}
      />

      {/* UPI QR for preview / pre-confirm */}
      <UpiQrDialog
        open={!!qrInvoice}
        onOpenChange={(o) => { if (!o) setQrInvoice(null); }}
        invoice={qrInvoice}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
      />

      {/* Shortcuts */}
      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent data-testid="shortcuts-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>Keyboard shortcuts</DialogTitle>
          </DialogHeader>
          <dl className="space-y-2 text-sm">
            {[
              ["/", "Focus patient search"],
              ["Enter", "Add top service result"],
              ["⌘/Ctrl + S", "Save / collect payment"],
              ["⌘/Ctrl + P", "Print last invoice"],
              ["?", "Show this dialog"],
              ["Esc", "Close dialogs"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <kbd className="px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono">{k}</kbd>
                <dd className="text-xs text-slate-600 text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </DialogContent>
      </Dialog>

      {/* Success modal */}
      <SuccessDialog
        invoice={successInvoice}
        clinicName={clinicName}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
        patient={patient}
        waConfigured={waConfigured}
        onClose={() => { setSuccessInvoice(null); resetForm(); }}
        onPrint={() => successInvoice && printInvoice(successInvoice.invoice_id)}
        onShareWhatsApp={() => successInvoice && shareWhatsApp(successInvoice)}
        onAutoSendWhatsApp={async () => {
          if (!successInvoice) return;
          try {
            await wa.sendInvoice(successInvoice.invoice_id);
            if (successInvoice.token_number) {
              // Fire-and-forget token message too
              wa.sendToken(successInvoice.invoice_id).catch(() => {});
            }
          } catch (_e) { /* toast already shown by lib */ }
        }}
        onShowQrForBalance={() => setQrInvoice(successInvoice)}
      />
    </div>
  );
}

// ============ Subcomponents ============
function ServiceChip({ svc, onClick, onPin, pinned }) {
  return (
    <div className="inline-flex items-stretch rounded-full border border-slate-200 bg-white hover:border-slate-300 transition-colors overflow-hidden" data-testid={`chip-${svc.service_id}`}>
      <button onClick={onClick} className="pl-3 pr-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
        {svc.name} · <span className="text-slate-500">{formatINR(svc.default_price)}</span>
      </button>
      <button
        onClick={onPin}
        title={pinned ? "Unpin" : "Pin to favorites"}
        className={`px-1.5 border-l border-slate-200 hover:bg-amber-50 ${pinned ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}
      >
        <Star className={`w-3 h-3 ${pinned ? "fill-amber-500" : ""}`} />
      </button>
    </div>
  );
}

function LineItemRow({ item, onChange, onRemove }) {
  return (
    <div className="px-3 sm:px-4 py-2.5 flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`line-item-${item.uid}`}>
      <Input
        value={item.description}
        onChange={(e) => onChange({ description: e.target.value })}
        className="h-9 flex-1 min-w-[160px] order-1 basis-full sm:basis-auto"
        data-testid={`line-desc-${item.uid}`}
      />
      <div className="flex items-center gap-2 order-2 w-full sm:w-auto">
        <Input
          type="number"
          min="1"
          step="1"
          value={item.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          className="h-9 w-14 text-center"
          title="Qty"
          data-testid={`line-qty-${item.uid}`}
        />
        <span className="text-slate-400 text-xs">×</span>
        <Input
          type="number"
          min="0"
          step="10"
          value={item.unit_price}
          onChange={(e) => onChange({ unit_price: e.target.value })}
          className="h-9 w-20 text-right"
          title="Unit price"
          data-testid={`line-price-${item.uid}`}
        />
        <div className="flex-1 sm:w-20 sm:flex-none text-right text-sm font-semibold text-slate-900" data-testid={`line-amt-${item.uid}`}>{formatINR(item.amount)}</div>
        <button onClick={onRemove} className="text-slate-300 hover:text-red-600 p-1" data-testid={`line-remove-${item.uid}`} title="Remove">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SplitPaymentDialog({ open, onOpenChange, total, initial, onApply, upiVpa }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) {
      setRows(initial.length > 0 ? initial.map((r) => ({ method: r.method, amount: String(r.amount) })) : [
        { method: "cash", amount: "" },
      ]);
    }
  }, [open, initial]);
  const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = +(total - sum).toFixed(2);
  const addRow = () => setRows((p) => [...p, { method: "cash", amount: "" }]);
  const update = (i, patch) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows((p) => p.filter((_, idx) => idx !== i));
  const apply = () => {
    const cleaned = rows.filter((r) => Number(r.amount) > 0).map((r) => ({ method: r.method, amount: Number(r.amount) }));
    if (cleaned.length === 0) { toast.error("Enter at least one amount"); return; }
    onApply(cleaned);
    onOpenChange(false);
  };
  const autoFill = () => {
    // fill remaining into last row
    setRows((p) => {
      if (p.length === 0) return [{ method: "cash", amount: String(total) }];
      const last = p.length - 1;
      const cur = Number(p[last].amount) || 0;
      const need = total - rows.slice(0, last).reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const next = [...p];
      next[last] = { ...next[last], amount: String(Math.max(need, 0)) };
      return next;
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="split-pay-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Split payment</DialogTitle>
          <DialogDescription>Mix cash, UPI, card. The remaining balance becomes &quot;Partial&quot; on the invoice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`split-row-${i}`}>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden order-1">
                {METHODS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => update(i, { method: m.key })}
                    className={`px-2 py-1.5 text-xs font-medium ${r.method === m.key ? "bg-blue-50 text-blue-700" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                    type="button"
                    title={m.label}
                    data-testid={`split-method-${i}-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min="0"
                step="10"
                value={r.amount}
                onChange={(e) => update(i, { amount: e.target.value })}
                placeholder="₹0"
                className="h-9 flex-1 min-w-[80px] order-2"
                data-testid={`split-amount-${i}`}
              />
              <button onClick={() => remove(i)} className="text-slate-300 hover:text-red-600 p-1 order-3 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={addRow} className="text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            <Button type="button" variant="ghost" size="sm" onClick={autoFill} className="text-xs">Auto-fill remaining</Button>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between gap-2 text-xs flex-wrap">
            <span>Total: <span className="font-semibold text-slate-900">{formatINR(total)}</span></span>
            <span>Collected: <span className="font-semibold text-emerald-700">{formatINR(sum)}</span></span>
            <span className={remaining > 0 ? "text-amber-700" : "text-emerald-700"}>{remaining > 0 ? `Balance: ${formatINR(remaining)}` : "Settled"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} className="bg-blue-600 hover:bg-blue-700" data-testid="split-apply">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuccessDialog({ invoice, clinicName, upiVpa, upiPayeeName, patient, waConfigured, onClose, onPrint, onShareWhatsApp, onAutoSendWhatsApp, onShowQrForBalance }) {
  if (!invoice) return null;
  const total = invoice.total ?? invoice.amount ?? 0;
  const balance = invoice.balance_due ?? 0;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="success-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Bill ready
          </DialogTitle>
          <DialogDescription>#{invoice.invoice_id?.slice(-8)} · {invoice.patient_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {invoice.token_number && (
            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white p-5 text-center" data-testid="success-token">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Patient token</div>
              <div className="text-5xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>#{invoice.token_number}</div>
              <div className="text-xs opacity-80 mt-1">{invoice.token_date}</div>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 px-4 py-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-semibold text-slate-900">{formatINR(total)}</span></div>
            {invoice.paid_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-700">{formatINR(invoice.paid_amount)}</span></div>}
            {balance > 0 && <div className="flex justify-between"><span className="text-slate-500">Balance due</span><span className="font-semibold text-amber-700">{formatINR(balance)}</span></div>}
          </div>

          {waConfigured && patient?.phone && (
            <Button
              onClick={onAutoSendWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="success-auto-whatsapp"
            >
              <Send className="w-4 h-4 mr-2" /> Auto-send on WhatsApp{invoice.token_number ? " (incl. token)" : ""}
            </Button>
          )}

          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" onClick={onPrint} className="text-xs" data-testid="success-print">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button variant="outline" onClick={onShareWhatsApp} disabled={!patient?.phone} className="text-xs text-green-700 hover:bg-green-50" data-testid="success-whatsapp" title="Open WhatsApp on this device">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Manual
            </Button>
            {balance > 0 && upiVpa?.includes("@") ? (
              <Button variant="outline" onClick={onShowQrForBalance} className="text-xs text-violet-700 hover:bg-violet-50" data-testid="success-collect-balance">
                <ScanLine className="w-4 h-4 mr-1.5" /> Collect balance
              </Button>
            ) : (
              <Button variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/invoices/${invoice.invoice_id}/pdf`, "_blank")} className="text-xs" data-testid="success-pdf">
                <Share2 className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="success-done">
            <Plus className="w-4 h-4 mr-2" /> New bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
